// Unpublished Work © 2020-2025 Deere & Company.

import * as actions from '../../action-list';
import {ApplicationStatus} from '../../state/struct/add-customer/application-status';
import {SaveError} from '../../state/struct/add-customer/save-error';

const setPageStatus = (state, action) => ApplicationStatus.update(state, {
    pageStatus: {
        $set: action.pageStatus
    }
});

const setIsPotentialMatchesModalShown = (state, action) => ApplicationStatus.update(state, {
    isPotentialMatchesModalShown: {
        $set: action.isPotentialMatchesModalShown
    }
});

const setIsDeceasedOrOutOfBusinessModalShown = (state, action) => ApplicationStatus.update(state, {
    isDeceasedOrOutOfBusinessModalShown: {
        $set: action.isDeceasedOrOutOfBusinessModalShown
    }
});

const setFillCustomerDetailsState = (state, action) => ApplicationStatus.update(state, {
    canFillCustomerDetailsFromFacets: {
        $set: action.canFillCustomerDetailsFromFacets
    }
});

const enableActionsState = (state) => ApplicationStatus.update(state, {
    canResetForm: {
        $set: true
    },
    isEditCustomerSaveButtonEnabled: {
        $set: true
    }
});

const disableResetForm = (state) => ApplicationStatus.update(state, {
    canResetForm: {
        $set: false
    },
    isEditCustomerSaveButtonEnabled: {
        $set: false
    },
    saveError: {
        $set: SaveError({})
    }
});

const updateStatusForEditCustomer = (state, action) => ApplicationStatus.update(state, {
    $merge: {
        canResetForm: false,
        isEditCustomerSaveButtonEnabled: false,
        addressCountryCode: action.addressCountryCode
    }
});

const setAddressCountryCode = (state, action) => ApplicationStatus.update(state, {
    addressCountryCode: {
        $set: action.addressCountryCode
    }
});

const setCountryErrorState = (state, action) => ApplicationStatus.update(state, {
    canShowCountryServiceError: {
        $set: action.showError
    }
});

const setCustomerProfileStatus = (state, action) => ApplicationStatus.update(state, {
    customerProfileStatus: {
        $set: action.customerProfileStatus
    }
});

const setSaveServiceError = (state, action) => ApplicationStatus.update(state, {
    saveError: {
        $set: action.saveError
    }
});

const setSpecificCountryStatesStatus = (state, action) => ApplicationStatus.update(state, {
    specificCountryStatesStatus: {
        $set: action.pageStatus
    }

});

const updateFullPageLoadingStatus = (state, action) => ApplicationStatus.update(state, {
    fullPageLoadingStatus: {
        $set: action.status
    }
});

const showConfirmationModalStatus = (state, action) =>
    ApplicationStatus.update(state, {
        showConfirmationModal: {
            $set: action.showConfirmationModal
        }
    });

const showReasonCaptureFormStatus = (state, action) =>
    ApplicationStatus.update(state, {
        showReasonCaptureForm: {
            $set: action.showReasonCaptureForm
        }
    });

const resetApplicationStatus = () => ApplicationStatus({});

const setSaveCustomerStatus = (state, action) => ApplicationStatus.update(state, {
    saveCustomerStatus: {
        $set: action.value
    }
});

const setDOoBIndicatorValue = (state, action) => ApplicationStatus.update(state, {
    deceasedOutOfBusinessFlowIndicator: {
        $set: action.deceasedOutOfBusinessFlowIndicator
    }
});

const updateCitiesDropdownPageStatus = (state, action) => ApplicationStatus.update(state, {
    cityDropdownStatus: {
        [action.addressType]: {
            $set: action.cityDropdownStatus
        }
    }
});

const setPreferredLanguageStatus = (state, action) => ApplicationStatus.update(state, {
    preferredLanguageStatus: {
        $set: action.pageStatus
    }
});

const resetAlertMessages = (state) => ApplicationStatus.update(state, {
    saveError: {
        $set: new SaveError({})
    }
});

export const applicationStatus = (state = ApplicationStatus({}), action = {}) => {
    const actionHandlers = {
        [actions.RESET_ADD_CUSTOMER_APPLICATION_STATUS]: resetApplicationStatus,
        [actions.RESET_ADD_CUSTOMER_FORM]: disableResetForm,
        [actions.RESET_ADD_CUSTOMER_CONTACT_PHONE]: enableActionsState,
        [actions.SET_EDIT_CUSTOMER]: updateStatusForEditCustomer,
        [actions.SET_ADD_CUSTOMER_EMAIL_INFO]: enableActionsState,
        [actions.SET_ADD_CUSTOMER_CONTACT_PHONE]: enableActionsState,
        [actions.SET_ADD_CUSTOMER_PAGE_STATUS]: setPageStatus,
        [actions.SET_ADDRESS_COUNTRY_CODE]: setAddressCountryCode,
        [actions.SET_DROPDOWN_CITIES_PAGE_STATUS]: updateCitiesDropdownPageStatus,
        [actions.SET_COUNTRY_SELECTION_ERROR]: setCountryErrorState,
        [actions.SET_CUSTOMER_PROFILE_STATUS]: setCustomerProfileStatus,
        [actions.SET_CUSTOMER_NAME_DETAILS]: enableActionsState,
        [actions.SET_MIDDLE_NAME_NOT_AVAILABLE_CHECK]: enableActionsState,
        [actions.SET_CUSTOMER_PREFIX_AND_GENERATION_DETAILS]: enableActionsState,
        [actions.SET_EMAIL_NOT_AVAILABLE_CHECK]: enableActionsState,
        [actions.SET_CUSTOMER_DETAILS_FROM_FACETS_FLAG]: setFillCustomerDetailsState,
        [actions.SET_TAX_ID_DETAILS]: enableActionsState,
        [actions.SET_GOVERNMENT_TAX_ID_DETAILS]: enableActionsState,
        [actions.SET_CUSTOMER_DATE_OF_BIRTH]: enableActionsState,
        [actions.SET_MAILING_ADDRESS]: enableActionsState,
        [actions.SET_SSN_DETAILS]: enableActionsState,
        [actions.SET_REGISTRANT_NUMBER_DETAILS]: enableActionsState,
        [actions.SET_US_CTZ_IND]: enableActionsState,
        [actions.SET_PREFERRED_LANGUAGE_LIST_STATUS]: setPreferredLanguageStatus,
        [actions.SET_PHYSICAL_ADDRESS]: enableActionsState,
        [actions.SET_POTENTIAL_MATCHES_MODAL_STATUS]: setIsPotentialMatchesModalShown,
        [actions.SET_DOOB_TIGHT_MATCH_MODAL_STATUS]: setIsDeceasedOrOutOfBusinessModalShown,
        [actions.SET_PROVIDED_PRIVACY_POLICY]: enableActionsState,
        [actions.SET_SAVE_CUSTOMER_STATUS]: setSaveCustomerStatus,
        [actions.SET_SAVE_SERVICE_ERROR]: setSaveServiceError,
        [actions.SET_STATES_OF_SPECIFIC_COUNTRY_STATUS]: setSpecificCountryStatesStatus,
        [actions.UPDATE_FULL_PAGE_LOADING_STATUS]: updateFullPageLoadingStatus,
        [actions.UPDATE_CONFIRMATION_MODAL_STATUS]: showConfirmationModalStatus,
        [actions.UPDATE_REASON_CAPTURE_FORM_STATUS]: showReasonCaptureFormStatus,
        [actions.UPDATE_DOOB_INDICATOR]: setDOoBIndicatorValue,
        [actions.RESET_REGISTRY_ERROR_MESSAGE]: resetAlertMessages,
        [actions.RESET_ALERT_MESSAGES]: resetAlertMessages,
        [actions.SET_BUSINESS_TAX_DETAILS]: enableActionsState,
        [actions.SET_CUSTOMER_FILING_DETAILS]: enableActionsState

    };
    const reducer = actionHandlers[action.type];

    return reducer ? reducer(state, action) : state;
};
